import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Flame, Check, Shield, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

interface StreakSystemMigrationModalProps {
  open: boolean;
  onClose: () => void;
}

const GOAL_OPTIONS = [
  { days: 2, label: 'Casual', description: '2 days/week' },
  { days: 3, label: 'Light', description: '3 days/week' },
  { days: 4, label: 'Regular', description: '4 days/week' },
  { days: 5, label: 'Committed', description: '5 days/week' },
  { days: 6, label: 'Dedicated', description: '6 days/week' },
  { days: 7, label: 'Daily', description: 'Every day' },
];

export function StreakSystemMigrationModal({ open, onClose }: StreakSystemMigrationModalProps) {
  const { user } = useAuth();
  const [selectedGoal, setSelectedGoal] = useState(7);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!user) return;

    setSaving(true);
    try {
      // Save reading goal preference
      const { error } = await supabase
        .from('user_reading_preferences')
        .upsert(
          {
            user_id: user.id,
            weekly_reading_goal: selectedGoal,
          },
          { onConflict: 'user_id' }
        );

      if (error) throw error;

      // Update current week's goal in user_streaks
      await supabase
        .from('user_streaks')
        .update({ weekly_goal_days: selectedGoal })
        .eq('user_id', user.id);

      toast.success('Your streak system has been upgraded! 🎉');
      onClose();
    } catch (error) {
      console.error('Error saving reading goal:', error);
      toast.error('Failed to save reading goal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Flame className="w-6 h-6 text-orange-500" />
            Your Streak System Just Got Better! 🎉
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            We've upgraded to a more flexible weekly streak system that adapts to your reading habits.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* What's New Section */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">What's Changed?</h3>
            <div className="grid gap-3">
              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium">Flexible Weekly Goals</p>
                  <p className="text-sm text-muted-foreground">
                    Set your own pace! Choose how many days per week you want to read (2-7 days).
                  </p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="font-medium">Streak Freezes</p>
                  <p className="text-sm text-muted-foreground">
                    Miss your goal by one day? A streak freeze automatically protects you!
                  </p>
                </div>
              </div>

              <div className="flex gap-3 items-start">
                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center flex-shrink-0">
                  <Flame className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="font-medium">Bonus Days for Exceeding Goals</p>
                  <p className="text-sm text-muted-foreground">
                    Read more than your goal? Every extra day adds to your streak!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Your Streak is Safe */}
          <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Check className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900 dark:text-green-100">
                  Your existing streak is preserved!
                </p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  All your progress carries over. You're just getting more flexibility in how you maintain it.
                </p>
              </div>
            </div>
          </div>

          {/* Goal Selection */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Choose Your Weekly Reading Goal</h3>
            <p className="text-sm text-muted-foreground">
              How many days per week do you want to commit to reading?
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {GOAL_OPTIONS.map((option) => (
                <Card
                  key={option.days}
                  onClick={() => setSelectedGoal(option.days)}
                  className={`
                    p-4 cursor-pointer transition-all hover:shadow-md
                    ${selectedGoal === option.days 
                      ? 'ring-2 ring-primary bg-primary/5' 
                      : 'hover:bg-muted/50'
                    }
                  `}
                >
                  <div className="flex flex-col items-center gap-2 text-center">
                    <div className="flex items-center gap-2">
                      <Flame className={`w-4 h-4 ${selectedGoal === option.days ? 'text-orange-500' : 'text-muted-foreground'}`} />
                      <span className="text-xl font-bold">{option.days}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{option.label}</p>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </div>
                    {selectedGoal === option.days && (
                      <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <Check className="w-3 h-3 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="font-semibold text-sm">📖 How it works:</p>
            <ul className="text-sm text-muted-foreground space-y-1 ml-4">
              <li>• Your streak continues as long as you hit your weekly goal</li>
              <li>• Activities that count: reading progress updates, reviews, focus sessions, ratings</li>
              <li>• Exceed your goal? Bonus days get added to your streak!</li>
              <li>• Miss your goal by one day? A freeze protects you automatically</li>
            </ul>
          </div>
        </div>

        <div className="flex gap-3 justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              setSelectedGoal(7);
              handleSave();
            }}
            disabled={saving}
          >
            Keep Daily (7 days)
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
          >
            {saving ? 'Saving...' : `Set ${selectedGoal}-Day Goal`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
